<template>
  <div class="tw-bg-gray-50 tw-rounded-lg tw-p-2">
    <div class="tw-flex tw-items-center tw-justify-between tw-mb-1">
      <div class="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide">Hardware</div>
      <button
        v-if="panelPhoto"
        class="tw-text-gray-400 hover:tw-text-blue-500 tw-transition-colors"
        title="Foto panelen installatie"
        @click="showPhoto = !showPhoto"
      >
        <svg class="tw-w-4 tw-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>
    </div>

    <!-- Panel photo overlay -->
    <div
      v-if="showPhoto && panelPhoto"
      class="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-80 tw-z-50 tw-flex tw-items-center tw-justify-center tw-cursor-pointer"
      @click="showPhoto = false"
    >
      <img :src="panelPhoto" alt="Panelen installatie" class="tw-max-w-[90vw] tw-max-h-[90vh] tw-object-contain tw-rounded-lg tw-shadow-2xl" />
      <button class="tw-absolute tw-top-4 tw-right-4 tw-text-white tw-bg-black tw-bg-opacity-50 tw-rounded-full tw-w-10 tw-h-10 tw-flex tw-items-center tw-justify-center hover:tw-bg-opacity-70 tw-transition-opacity">
        <svg class="tw-w-6 tw-h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <div class="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-0.5 tw-text-sm">
      <template v-if="meterDetail.num_of_panels || meterDetail.panel_type">
        <span class="tw-text-gray-500">Panelen</span>
        <span class="tw-font-medium">
          {{ meterDetail.num_of_panels ? `${meterDetail.num_of_panels}×` : '' }}
          {{ meterDetail.panel_type || '' }}
        </span>
      </template>
      <!-- the selected meter: Cast4all communicator or polling meter type, see MeterPanel hardwareMeter -->
      <template v-if="meter">
        <span class="tw-text-gray-500">Meter</span>
        <span class="tw-font-medium" :title="meter.title">{{ meter.value }}</span>
      </template>
      <template v-if="meterDetail.inverter_type">
        <span class="tw-text-gray-500">Omvormer</span>
        <span class="tw-font-medium">{{ meterDetail.inverter_type }}</span>
      </template>
      <template v-if="meterDetail.shadow_loss != null">
        <span class="tw-text-gray-500">Schaduw</span>
        <span class="tw-font-medium">{{ meterDetail.shadow_loss }}%</span>
      </template>
      <template v-if="meterDetail.expected_production">
        <span class="tw-text-gray-500">Verwacht/jaar</span>
        <span class="tw-font-medium">{{ Math.round(meterDetail.expected_production / 1000) }} kWh</span>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import fetchData from '@/util/fetch'

const props = defineProps({
  meterDetail: { type: Object, required: true },
  // { value, title } of the selected meter, null for SolarEdge
  meter: { type: Object, default: null },
})

const showPhoto = ref(false)

// ─── Installation photo from tickets ────────────────────────────────────────

const ticketQuery = computed(() => {
  if (!props.meterDetail?.house_number || !props.meterDetail?.zip_code) return null
  const zip = (props.meterDetail.zip_code || '') + (props.meterDetail.zip_code_ext || '')
  return `${props.meterDetail.house_number}${zip}`.replace(/\s/g, '')
})

const { data: ticketsList } = useQuery({
  queryKey: computed(() => ['installationTickets', ticketQuery.value]),
  queryFn: async () => {
    const result = await fetchData(
      `/tickets/api/v1/tickets?view=list&type=installation&status=approved&query=${encodeURIComponent(ticketQuery.value)}&limit=1`
    )
    return result?.data ?? []
  },
  enabled: computed(() => !!ticketQuery.value),
  refetchOnWindowFocus: false,
  staleTime: 30 * 60 * 1000,
})

const ticketId = computed(() => ticketsList.value?.[0]?.id ?? null)

const { data: ticketDetail } = useQuery({
  queryKey: computed(() => ['ticketDetail', ticketId.value]),
  queryFn: async () => {
    const result = await fetchData(`/tickets/api/v1/tickets/${ticketId.value}`)
    return result?.data ?? null
  },
  enabled: computed(() => !!ticketId.value),
  refetchOnWindowFocus: false,
  staleTime: 30 * 60 * 1000,
})

const panelPhoto = computed(() => {
  const ticket = ticketDetail.value
  const images = ticket?.data?.output?.images || ticket?.output?.images
  if (!images) return null
  const photo = images.find(img => img.type === 'panels_installed_1' && img.url)
    || images.find(img => img.type?.startsWith('panels_installed') && img.url)
  if (photo) return photo.url
  const inputImages = ticket?.data?.input?.images || ticket?.input?.images
  if (inputImages) {
    const inputPhoto = inputImages.find(img => img.url)
    return inputPhoto?.url ?? null
  }
  return null
})
</script>
